/**
 * Header del chat: avatar de la empresa-cliente + nombre + lista compacta de
 * miembros con avatares mini (tooltip con nombre y rol).
 */
import { Building2 } from "lucide-react";
import type { Room } from "@/api/rooms";
import type { User } from "@/api/users";
import { Avatar } from "./Avatar";

type Props = {
  room: Room;
  members: User[];
  typingUserIds?: string[];
};

function roleLabel(role: User["role"]): string {
  return (
    {
      superadmin: "Súper admin",
      tenant_admin: "Admin",
      agent: "Agente",
      customer: "Cliente",
    } as const
  )[role] ?? role;
}

export function ChatHeader({ room, members, typingUserIds = [] }: Props) {
  const typingMembers = members.filter((m) => typingUserIds.includes(m.id));
  const visibleMembers = members.slice(0, 6);
  const overflow = members.length - visibleMembers.length;

  return (
    <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-2.5">
      <Avatar name={room.customer_company_name} seed={room.id} size="md" />
      <div className="min-w-0 flex-1">
        <h2 className="truncate text-sm font-semibold text-slate-900">{room.customer_company_name}</h2>
        {typingMembers.length > 0 ? (
          <p className="truncate text-xs text-brand-600 italic">
            {typingMembers.map((m) => m.full_name.split(" ")[0]).join(", ")}
            {typingMembers.length === 1 ? " está escribiendo…" : " están escribiendo…"}
          </p>
        ) : (
          <p className="truncate text-xs text-slate-500">
            {members.length} {members.length === 1 ? "miembro" : "miembros"}
            {room.description ? ` · ${room.description}` : ""}
          </p>
        )}
      </div>
      <div className="hidden items-center -space-x-2 sm:flex">
        {visibleMembers.map((m) => (
          <span
            key={m.id}
            title={`${m.full_name} (${roleLabel(m.role)})`}
            className="ring-2 ring-white"
          >
            <Avatar name={m.full_name} seed={m.id} size="sm" url={m.avatar_url ?? undefined} />
          </span>
        ))}
        {overflow > 0 && (
          <span
            className="inline-flex size-7 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-700 ring-2 ring-white"
            title={`y ${overflow} más`}
          >
            +{overflow}
          </span>
        )}
      </div>
      <div className="sm:hidden">
        <Building2 className="size-5 text-slate-400" />
      </div>
    </div>
  );
}
